/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/**
 * Returns true if the navigation was an org change.
 * @param {string} oldPath window location pathname.
 * @param {string} newPath history location pathname.
 * @returns {boolean} Returns true if the navigation was an org change.
 */
export function isOrgChange(oldPath, newPath) {
  let oldPathArr = oldPath.split('/');
  let newPathArr = newPath.split('/');

  // If the length is not the same, more has changed than just the tenant meaning
  // it is not an org change.
  if (oldPathArr.length !== newPathArr.length) {
    return false;
  }
  // Because we are splitting by '/' arr[0] will be "" and arr[1] will be the tenant.
  for (let i = 2; i < oldPathArr.length - 1; i++) {
    if (oldPathArr[i] !== newPathArr[i]) {
      return false;
    }
  }
  return oldPathArr[1] !== newPathArr[1];
}

/**
 * Get name of org.
 * @param {string} org String with org.
 * @param {Array} map List of orgs.
 * @returns {string} Org name or unknown.
 */
export function getOrgName(org, map) {
  let [ident, authSrc] = org.split('@');
  let s = map.find(e => e.orgRef.ident === ident && e.orgRef.authSrc === authSrc);
  return s ? s.orgName : 'unknown';
}

/**
 * Copies a property from object payload to another object reduced payload.
 * @param {object} toObject Destination object
 * @param {object} fromObject Original object.
 * @param {string} property String denoting the property to copy over.
 */
export function copyProperty(toObject, fromObject, property) {
  function copy(toObject, fromObject, parts) {
    let currentKey = parts[0];
    if (parts.length > 1) {
      toObject[currentKey] = toObject[currentKey] || {};
      fromObject = fromObject[currentKey];
      return copy(toObject[currentKey], fromObject, parts.slice(1));
    }
    toObject[currentKey] = fromObject[currentKey];
  }
  copy(toObject, fromObject, property.split('.'));
}

/**
 * Gets the preferred avatar URL from the Behance payload.
 * @param {object} avatar Behance avatar information.
 * @param {array} sizes Array of preferred sizes, ordered by preference.
 * @returns {object} Avatar URL and Data URL.
 */
export function getAvatarUrls(avatar, sizes) {
  let url;
  if (avatar && avatar.images) {
    sizes.some(size => url = avatar.images[String(size)]);
    return {
      avatar: url,
      avatarSrc: avatar.encodedImage ? `data:image/jpeg;base64, ${avatar.encodedImage}` : undefined
    };
  }
  return {};
}

let gainsightPromise = null;

/**
 * Promise for grabbing the gainsight callback off of the window. This will
 * be used to trigger when a new app is loaded. This method only runs the
 * promise on the first run and caches the result for future calls.
 * @param {boolean} ignorePromise Ignores the existing promise. FOR TESTING ONLY.
 * @returns {Promise} The promise that will include the callback.
 */
export let waitForGainsightCallback = ignorePromise => {
  if (gainsightPromise && !ignorePromise) {
    return gainsightPromise;
  }
  return gainsightPromise = new Promise(res => {
    let cnt = 0;
    function pollForCallback() {
      cnt++;
      if (window.initGainsight || cnt >= 10) {
        return res(window.initGainsight || function () {});
      }
      setTimeout(pollForCallback, 500);
    }
    setTimeout(pollForCallback, 500);
  });
};
